import { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Header } from '../components/landing/Header';
import { Footer } from '../components/landing/Footer';

const WEBHOOK_URL = process.env.REACT_APP_WEBHOOK_URL || 'https://cloud.activepieces.com/api/v1/webhooks/CGgeAUwuOqP14fcBzYXyI';

export default function RequestDemoPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    businessName: '',
    country: '',
    fleetSize: '',
    website: '',
    message: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Required';
    if (!form.email.trim()) newErrors.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Invalid email';
    if (!form.businessName.trim()) newErrors.businessName = 'Required';
    if (!form.country.trim()) newErrors.country = 'Required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'request_demo',
          submitted_at: new Date().toISOString(),
          full_name: form.fullName.trim(),
          email: form.email.trim(),
          business_name: form.businessName.trim(),
          country: form.country.trim(),
          fleet_size: form.fleetSize || null,
          website: form.website.trim() || null,
          message: form.message.trim() || null,
        }),
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#faf9f6]">
        <Header />
        <main className="pt-[140px] pb-[100px] lg:pt-[160px] lg:pb-[120px]">
          <div className="max-w-[520px] mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#2d6a5a]/10 flex items-center justify-center">
                <CheckCircle size={20} className="text-[#2d6a5a]" />
              </div>
              <h1 className="font-heading text-[1.6rem] lg:text-[2rem] font-bold text-stone-900 tracking-[-0.01em]">
                Demo request received
              </h1>
            </div>
            <p className="text-[0.95rem] text-stone-500 leading-[1.75] mt-4">
              Thanks for your interest in bikes.rentals.
            </p>
            <p className="text-[0.95rem] text-stone-500 leading-[1.75] mt-3">
              We will contact you shortly to schedule a demo session.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f6]">
      <Header />
      <main className="pt-[140px] pb-[100px] lg:pt-[160px] lg:pb-[120px]">
        <div className="max-w-[520px] mx-auto px-6 lg:px-8">
          <h1 className="font-heading text-[1.85rem] lg:text-[2.5rem] font-bold text-stone-900 leading-[1.1] tracking-[-0.02em]">
            Request a demo
          </h1>

          <p className="mt-5 text-[0.95rem] text-stone-500 leading-[1.75]">
            If you would like a walkthrough of the platform before discussing your setup, we are happy to show you how bikes.rentals works in practice.
          </p>

          <p className="mt-4 text-[0.95rem] text-stone-500 leading-[1.75]">
            In the demo we typically cover:
          </p>
          <ul className="mt-2.5 space-y-1.5">
            {[
              'Booking flow from the customer perspective',
              'Daily operations dashboard',
              'Issue and maintenance tracking',
              'Delivery and self-service options',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-[7px] w-[5px] h-[5px] rounded-full bg-stone-400 flex-shrink-0" />
                <span className="text-stone-500 text-[0.9rem] leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>

          {/* Qualifier */}
          <p className="mt-10 mb-5 text-[0.8rem] text-stone-400 uppercase tracking-wider font-medium">
            For operators evaluating rental software.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="fullName" className="text-[0.84rem] text-stone-700 font-medium">
                Full name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="fullName"
                value={form.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                className={`mt-1.5 bg-white border-stone-200 focus:border-[#2d6a5a] focus:ring-[#2d6a5a]/20 rounded-lg h-11 ${
                  errors.fullName ? 'border-red-300' : ''
                }`}
              />
              {errors.fullName && <p className="text-[0.78rem] text-red-400 mt-1">{errors.fullName}</p>}
            </div>

            <div>
              <Label htmlFor="email" className="text-[0.84rem] text-stone-700 font-medium">
                Email <span className="text-red-400">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`mt-1.5 bg-white border-stone-200 focus:border-[#2d6a5a] focus:ring-[#2d6a5a]/20 rounded-lg h-11 ${
                  errors.email ? 'border-red-300' : ''
                }`}
              />
              {errors.email && <p className="text-[0.78rem] text-red-400 mt-1">{errors.email}</p>}
            </div>

            <div>
              <Label htmlFor="businessName" className="text-[0.84rem] text-stone-700 font-medium">
                Business name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="businessName"
                value={form.businessName}
                onChange={(e) => handleChange('businessName', e.target.value)}
                className={`mt-1.5 bg-white border-stone-200 focus:border-[#2d6a5a] focus:ring-[#2d6a5a]/20 rounded-lg h-11 ${
                  errors.businessName ? 'border-red-300' : ''
                }`}
              />
              {errors.businessName && <p className="text-[0.78rem] text-red-400 mt-1">{errors.businessName}</p>}
            </div>

            <div>
              <Label htmlFor="country" className="text-[0.84rem] text-stone-700 font-medium">
                Country <span className="text-red-400">*</span>
              </Label>
              <Input
                id="country"
                value={form.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className={`mt-1.5 bg-white border-stone-200 focus:border-[#2d6a5a] focus:ring-[#2d6a5a]/20 rounded-lg h-11 ${
                  errors.country ? 'border-red-300' : ''
                }`}
              />
              {errors.country && <p className="text-[0.78rem] text-red-400 mt-1">{errors.country}</p>}
            </div>

            <div>
              <Label className="text-[0.84rem] text-stone-700 font-medium">
                Fleet size
              </Label>
              <Select
                value={form.fleetSize}
                onValueChange={(val) => handleChange('fleetSize', val)}
              >
                <SelectTrigger className="mt-1.5 bg-white border-stone-200 focus:border-[#2d6a5a] focus:ring-[#2d6a5a]/20 rounded-lg h-11">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-20">1–20 bikes</SelectItem>
                  <SelectItem value="20-50">20–50 bikes</SelectItem>
                  <SelectItem value="50-150">50–150 bikes</SelectItem>
                  <SelectItem value="150+">150+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="website" className="text-[0.84rem] text-stone-700 font-medium">
                Website <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="website"
                value={form.website}
                onChange={(e) => handleChange('website', e.target.value)}
                className="mt-1.5 bg-white border-stone-200 focus:border-[#2d6a5a] focus:ring-[#2d6a5a]/20 rounded-lg h-11"
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-[0.84rem] text-stone-700 font-medium">
                Message <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="message"
                value={form.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="mt-1.5 bg-white border-stone-200 focus:border-[#2d6a5a] focus:ring-[#2d6a5a]/20 rounded-lg min-h-[100px] resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#2d6a5a] hover:bg-[#245549] text-white rounded-full h-12 text-[0.92rem] font-medium hover:shadow-lg hover:shadow-[#2d6a5a]/15 mt-2 disabled:opacity-60"
              style={{ transition: 'background-color 0.2s, box-shadow 0.3s' }}
            >
              {submitting ? 'Submitting...' : 'Request demo'}
            </Button>

            {submitError && (
              <p className="text-[0.82rem] text-red-400 text-center">{submitError}</p>
            )}

            <p className="text-[0.8rem] text-stone-400 text-center">
              We typically respond within one business day.
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
